import defaultImg from "@assets/png/img.png";
import Button from "@components/common/Button";
import ListTable from "@components/common/ListTable";
import Pagination from "@components/common/pagination";
import SearchInput from "@components/common/SearchInput";
import DeleteIcon from "@components/icons/DeleteIcon";
import EditIcon from "@components/icons/EditIcon";
import PlusIcon from "@components/icons/PlusIcon.jsx";
import ModalAlert from "@components/Modals/ModalAlert";
import { ModalCorporateAllowance } from "@components/Modals/ModalCorporateAllowance.jsx";
import ModalDelete from "@components/Modals/ModalDelete";
import { useIsMobile } from "@components/mediaQuery/useIsMobile";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { setLoadingVesselUsers, setVesselPhoto, setVesselUsers } from "@store/modules/vesselsSlice";
import { checkPermission } from "@utils/permissions";
import {
	IS_ACTIVATION_NAME,
	IS_ACTIVATION_ROLE,
	IS_ADMIN_NAME,
	IS_ADMIN_ROLE,
	IS_DISTRIBUTOR_ROLE,
} from "@utils/user_roles";
import { Tooltip } from "flowbite-react";
import { latLngToCell } from "h3-js";
import { debounce } from "lodash";
import ProjectOverviewContent from "pages/ManagementPool/ProjectOverviewContent";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FLAGS } from "../../../config/growthbook/flags.js";
import {
	fetchVesselUsers,
	fetchvesselItemwithredirect,
	setVesselDeletePhoto,
	setVesselPhotoRequest,
} from "../../../services/vesselService";

const MapComponent = lazy(() => import("@components/map/MapComponent"));
const CustomAccordion = lazy(() => import("@components/common/CustomAccordion"));

const OverviewSummary = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const vesselData = useSelector((state) => state.modules.vessels?.vesselDetails);
	const loadingVesselDetails = useSelector(
		(state) => state.modules.vessels.starlink.loadingVesselDetails,
	);
	const vesselId = useSelector((state) => state.modules.vessels?.selectedVessel?.id);

	const {
		loading,
		data: usersData,
		pagination,
	} = useSelector((state) => state.modules?.vessels?.users);

	const { perPage, totalItems, totalPage } = pagination;

	const accessUsersVisible = useFeatureIsOn(FLAGS.vessels.overview.accessUsersVisible);

	const [currentPage, setCurrentPage] = useState(1);
	const hasStarlinks = Array.isArray(vesselData?.starlinks) && vesselData.starlinks.length > 0;
	const hasSmartBoxs = Array.isArray(vesselData?.smartboxes) && vesselData?.smartboxes?.length > 0;
	const geoLocation = vesselData?.starlinks?.[0]?.geo_location || [];
	const hasIntelsats = Array.isArray(vesselData?.intelsats) && vesselData.intelsats.length > 0;
	const lastGeoLocation = vesselData?.last_geo_location;
	const h3Resolution = 9;

	// Normalize H3 safely
	const normalizeH3 = (value) => {
		if (value == null) return null;
		try {
			if (typeof value === "string") {
				const v = value.trim().toLowerCase();
				if (v.includes("error")) return null;
				if (v.startsWith("0x")) return v;
				if (/^\d+$/.test(v)) return `0x${BigInt(v).toString(16)}`;
				return null;
			}
			if (typeof value === "number") return `0x${BigInt(value).toString(16)}`;
			return null;
		} catch {
			return null;
		}
	};

	const currentH3 = normalizeH3(geoLocation?.h3cellid);

	const lat = Number(lastGeoLocation?.latitude);
	const lng = Number(lastGeoLocation?.longitude);

	const isValidLatLng =
		Number.isFinite(lat) &&
		Number.isFinite(lng) &&
		lat >= -90 &&
		lat <= 90 &&
		lng >= -180 &&
		lng <= 180;

	const lastGeoH3Cell = isValidLatLng
		? `0x${latLngToCell(lat, lng, h3Resolution).toString(16)}`
		: null;

	if (!isValidLatLng && lastGeoLocation) {
		console.warn("Invalid lat/lng, skipping H3 conversion:", lastGeoLocation);
	}

	const firstValidStarlink =
		(geoLocation?.timestamp ?? 0) > (lastGeoLocation?.timestamp ?? 0)
			? currentH3 || lastGeoH3Cell
			: lastGeoH3Cell || currentH3;

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [error, setError] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const { token, permissions } = useSelector((state) => state.auth.user);
	const isMobile = useIsMobile();

	const [isOpen, setIsOpen] = useState(false);
	const [scale, setScale] = useState(1);
	const imageRef = useRef(null);

	const userroleId = useSelector((state) => state.auth.user.role?.id);
	const userrole = useSelector((state) => state.auth.user.role?.name);
	const userparentrole = useSelector((state) => state.auth.user.role?.parent_id);
	const [searchTerms, setSearchTerms] = useState({
		search: "",
	});
	const [openAccordions, setOpenAccordions] = useState({
		"current-location": true,
		"general-information": true,
		"total-usage": true,
		"total-usage-scap": true,
	});

	const [corporateModalData, setCorporateModalData] = useState({
		open: false,
		vesselId: null,
		vesselName: null,
	});

	const vesselsSite = permissions.find((resource) => resource.resource_name === "vessels_site");
	const authorizedPermissions = vesselsSite?.permissions || [];

	const canEditVessels = checkPermission(authorizedPermissions, "update vessels_site");
	const isNicknameVisible = vesselData?.business_unit?.name === "IEC TELECOM NORWAY";

	const handleEdit = async (event) => {
		const file = event.target.files[0];
		if (file.size > 2 * 1024 * 1024) {
			setError("File size exceeds 2MB.");
			setShowErrorModal(true);
			return;
		}

		setError("");
		setIsEditing(true);

		const formData = new FormData();
		formData.append("photo", file);
		formData.append("_method", "put");
		const type = "edit";
		try {
			const response = await setVesselPhotoRequest(
				type,
				vesselData.id,
				formData,
				token,
				dispatch,
				navigate,
			);

			if (response.status === 200) {
				if (response?.data?.data?.photo) {
					const photoUrl = response.data.data.photo;
					await dispatch(setVesselPhoto(photoUrl));
				}
				setShowSuccessModal(true);
			} else {
				throw new Error("Failed to upload photo");
			}
		} catch (error) {
			console.error("Error uploading photo:", error);
			setError(error.response?.data?.message || "Something went wrong. Try again.");
			setShowErrorModal(true);
		} finally {
			setIsEditing(false);
		}
	};

	const handleDelete = async () => {
		const formData = new FormData();
		formData.append("delete_photo", 1);
		formData.append("_method", "put");

		const type = "delete";
		try {
			const response = await setVesselDeletePhoto(vesselData.id, token, dispatch, navigate);

			if (response.status === 200 || response.status === 204 || response.status === 202) {
				await dispatch(setVesselPhoto(response?.data?.data?.photo));
				setShowSuccessModal(true);
			} else {
				throw new Error("Failed to delete photo");
			}
		} catch (error) {
			console.error("Error deleting photo:", error);
			setError(error.response?.data?.message || "Something went wrong. Try again.");
			setShowErrorModal(true);
		} finally {
			setShowDeleteModal(false);
		}
	};
	const toggleAccordion = (id) => {
		setOpenAccordions((prevState) => ({
			...prevState,
			[id]: !prevState[id],
		}));
	};

	let now = new Date();
	let currentMonth = now.getUTCMonth() + 1;

	let currentYear = now.getUTCFullYear();
	const usageByServiceLine = new Map();

	vesselData?.starlinks?.forEach((starlink) => {
		const sub = starlink.subscriptions?.find(
			(s) => s.month === currentMonth && s.year === currentYear,
		);
		const serviceLine = starlink.service_line_number;

		if (sub && !usageByServiceLine.has(serviceLine)) {
			usageByServiceLine.set(serviceLine, parseFloat(sub.usage) || 0);
		}
	});

	const totalUsage = [...usageByServiceLine.values()].reduce((acc, val) => acc + val, 0).toFixed(2);

	const serviceLineCount = {};

	vesselData?.starlinks?.forEach((starlink) => {
		const serviceLine = starlink.service_line_number;
		if (serviceLine) {
			serviceLineCount[serviceLine] = (serviceLineCount[serviceLine] || 0) + 1;
		}
	});

	const kitsWithLabels = vesselData?.starlinks?.map((starlink) => {
		const serviceLine = starlink?.service_line_number;
		const isShared = serviceLineCount[serviceLine] > 1;

		return {
			...starlink,
			usageLabel: isShared ? "Service Line Usage:" : "Starlink Usage:",
		};
	});

	// const totalUsage =
	//   vesselData?.starlinks
	//     ?.flatMap((starlink) =>
	//       starlink.subscriptions?.filter((sub) => sub.month === currentMonth)
	//     )
	//     .reduce((sum, sub) => sum + (parseFloat(sub?.usage) || 0), 0)
	//     .toFixed(2) || "0.00";

	const handleImageClick = () => {
		setIsOpen(true);
		setScale(1);
	};

	const handleClose = () => {
		setIsOpen(false);
		setScale(1);
	};

	const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 2));
	const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
	const resetZoom = () => setScale(1);

	const handleWheel = (e) => {
		e.preventDefault();
		if (e.deltaY < 0) zoomIn();
		else zoomOut();
	};

	useEffect(() => {
		const img = imageRef.current;
		if (img) img.addEventListener("wheel", handleWheel, { passive: false });
		return () => {
			if (img) img.removeEventListener("wheel", handleWheel);
		};
	}, []);

	const loadVesselusersData = async (signal) => {
		// Use GrowthBook feature flags fetch vessel access users api
		const isAllowed =
			accessUsersVisible && [IS_ADMIN_ROLE, IS_DISTRIBUTOR_ROLE].includes(userroleId);

		if (!isAllowed) return;
		dispatch(setLoadingVesselUsers(true));
		try {
			const result = await fetchVesselUsers(
				token,
				vesselId,
				dispatch,
				navigate,
				currentPage,
				perPage,
				searchTerms.search,
				1,
				signal,
			);

			dispatch(setVesselUsers(result));
		} catch (error) {
			if (error.name === "CanceledError") return;
			console.error("Failed to load vessel users:", error);
		} finally {
			dispatch(setLoadingVesselUsers(false));
		}
	};

	const debouncedFetch = useMemo(
		() =>
			debounce((signal) => {
				loadVesselusersData(signal);
			}, 300),
		[token, vesselId, currentPage, perPage, searchTerms.search],
	);
	useEffect(() => {
		const controller = new AbortController();

		debouncedFetch(controller.signal);

		return () => {
			debouncedFetch.cancel();
			controller.abort();
		};
	}, [debouncedFetch]);

	const columns = [
		{ header: "Email", field: "email" },
		{ header: "Firstname ", field: "first_name" },
		{ header: "Lastname", field: "last_name" },
		{ header: "Notification enabled", field: "enable_notification" },
	];

	const handleRowClick = (id, name) => {
		null;
	};
	const handlePageChange = (newPage) => {
		if (newPage !== currentPage && newPage >= 1 && newPage <= totalPage) {
			setCurrentPage(newPage);
		}
	};

	const startItemIndex = (currentPage - 1) * perPage + 1;
	const endItemIndex = Math.min(currentPage * perPage, totalItems);

	const handleSearchChange = (id, value) => {
		setSearchTerms((prevTerms) => {
			const updatedTerms = {
				...prevTerms,
				search: value,
			};
			setCurrentPage(1);

			return updatedTerms;
		});
	};

	const handleClearSearch = () => {
		setSearchTerms({ search: "" });
		setCurrentPage(1);
	};

	const handleIncreaseAllowance = (vesselId, vesselName) => {
		setCorporateModalData({
			open: true,
			vesselId,
			vesselName,
		});
	};

	const id = vesselData?.id;
	const fetchConfigurations = async () => {
		const url = `/vessel/${id}`;
		await fetchvesselItemwithredirect(id, "", token, dispatch, navigate, url);
	};

	return (
		<div>
			{loadingVesselDetails ? (
				<div className="flex h-64 items-center justify-center">
					<div className="h-32 w-32 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
				</div>
			) : (
				<Suspense
					fallback={
						<div className="flex h-64 items-center justify-center">
							<div className="h-32 w-32 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
						</div>
					}
				>
					<>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-2 xl:lg:grid-cols-2 2xl:lg:grid-cols-2">
							{firstValidStarlink && (
								<CustomAccordion
									title="CURRENT LOCATION"
									id={"current-location"}
									isOpen={openAccordions[`current-location`]}
									toggleAccordion={() => toggleAccordion(`current-location`)}
									classCss={`${openAccordions[`current-location`] ? "bg-navbar-bg" : ""} border-0`}
									isMobile={isMobile}
								>
									<MapComponent h3cellid={firstValidStarlink} />
								</CustomAccordion>
							)}

							<CustomAccordion
								title="GENERAL INFORMATION"
								id={"general-information"}
								isOpen={openAccordions[`general-information`]}
								toggleAccordion={() => toggleAccordion(`general-information`)}
								classCss={`${openAccordions[`general-information`] ? "bg-navbar-bg" : ""} border-0`}
								isMobile={isMobile}
							>
								<div className="relative max-h-[400px] min-h-[200px] w-full overflow-hidden sm:max-h-[350px] md:max-h-[400px] lg:max-h-[800px] xl:max-h-[800px]">
									<div className="relative flex w-full items-center justify-center">
										{/* <img
                    className="h-auto max-w-full mx-auto"
                    src={
                      vesselData?.photo
                        ? `${vesselData?.photo}?t=${new Date().getTime()}`
                        : "/src/assets/png/img.png"
                    }
                    alt="Vessel image"
                  /> 
                  */}

										<>
											{/* Image Preview */}
											<div className="relative flex w-full items-center justify-center">
												<img
													onClick={handleImageClick}
													className="mx-auto max-h-screen max-w-full cursor-zoom-in object-contain transition-transform duration-200 hover:scale-105"
													src={
														vesselData?.photo
															? `${vesselData.photo}?t=${new Date().getTime()}`
															: defaultImg
													}
													alt="Vessel preview"
												/>
											</div>

											{/* Zoom Modal */}
											{isOpen && (
												<div
													className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-dark-blue bg-opacity-80 p-4"
													onClick={handleClose}
												>
													<div
														onClick={(e) => e.stopPropagation()}
														className="relative inline-block max-h-full max-w-full"
													>
														{/* Close Button (now relative to image container) */}
														<button
															onClick={(e) => {
																e.stopPropagation();
																handleClose();
															}}
															className="-translate-x-1/2 fixed top-4 left-1/2 z-[9999] flex h-12 w-12 items-center justify-center rounded-full bg-gray-300 text-black shadow-lg hover:bg-hover-card"
														>
															✕
														</button>

														<div
															onClick={(e) => e.stopPropagation()}
															className="relative max-h-full max-w-full"
														>
															<img
																ref={imageRef}
																src={
																	vesselData?.photo
																		? `${vesselData.photo}?t=${new Date().getTime()}`
																		: defaultImg
																}
																alt="Zoomed Vessel"
																className="transform object-contain transition-transform duration-200"
																style={{ transform: `scale(${scale})` }}
															/>
														</div>
													</div>

													{/* Zoom Controls */}
													<div className="absolute bottom-6 flex gap-3">
														<button
															onClick={(e) => {
																e.stopPropagation();
																zoomOut();
															}}
															className="rounded bg-button-bg px-3 py-1 text-white hover:bg-hover-card"
														>
															-
														</button>
														<button
															onClick={(e) => {
																e.stopPropagation();
																resetZoom();
															}}
															className="rounded bg-button-bg px-3 py-1 text-white hover:bg-hover-card"
														>
															Reset
														</button>
														<button
															onClick={(e) => {
																e.stopPropagation();
																zoomIn();
															}}
															className="rounded bg-button-bg px-3 py-1 text-white hover:bg-hover-card"
														>
															+
														</button>
													</div>
												</div>
											)}
										</>
										{canEditVessels && (
											<div className="absolute right-2 bottom-2 flex space-x-2">
												<input
													type="file"
													accept="image/*"
													id="upload-photo"
													style={{ display: "none" }}
													onChange={handleEdit}
												/>
												<Button
													text={
														<span className="hidden md:inline">
															{isEditing ? "Saving..." : "Edit"}
														</span>
													}
													icon={EditIcon}
													className={`${
														isEditing ? "animate-pulse" : ""
													} bg-dark-blue text-white hover:text-white hover:opacity-90`}
													onClick={() => document.getElementById("upload-photo").click()}
													disabled={isEditing}
												/>
												<Button
													text={<span className="hidden md:inline">Delete photo</span>}
													icon={DeleteIcon}
													className={"bg-red-700 text-white hover:bg-red-600"}
													onClick={() => setShowDeleteModal(true)}
												/>
											</div>
										)}
									</div>
								</div>

								{hasStarlinks && (
									<div
										className={`custom-scrollbar col-span-2 overflow-y-auto ${
											vesselData?.starlinks.length > 2 ? "max-h-[300px]" : ""
										}`}
									>
										{kitsWithLabels.map((starlink, index) => (
											<div
												key={starlink.id}
												className={`px-4 pt-4 ${
													index !== vesselData?.starlinks.length - 1
														? "border-gray-700 border-b"
														: ""
												}`}
											>
												{/* <div className="mb-2 flex justify-between">
                        <span className="font-medium text-gray-300">
                          Service line number:
                        </span>
                        <span className="text-gray-400">
                          {starlink.service_line_number}
                        </span>
                      </div> */}
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Kit Number:</span>
													<span className="text-gray-400">{starlink.kit_number}</span>
												</div>
												{isNicknameVisible && starlink?.kit_nickname && (
													<div className="mb-2 flex justify-between">
														<span className="font-medium text-gray-300">Kit Nickname:</span>
														<span className="text-gray-400">{starlink?.kit_nickname}</span>
													</div>
												)}
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">
														{/* Starlink Usage: */}
														{starlink.usageLabel}
													</span>
													<span className="text-gray-400">
														{starlink?.subscriptions?.length > 0
															? (() => {
																	const currentSub = starlink.subscriptions.find(
																		(s) => s?.month === currentMonth && s?.year === currentYear,
																	);

																	return currentSub ? `${currentSub.usage} GB` : "N/A";
																})()
															: "N/A"}
													</span>
												</div>
												{starlink?.service_line_number && (
													<div className="mb-2 flex justify-between">
														<span className="font-medium text-gray-300">Service Line:</span>
														<span className="text-gray-400">{starlink?.service_line_number}</span>
													</div>
												)}

												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Status:</span>
													<span
														className={`me-2 rounded px-2.5 py-0.5 font-medium text-xs ${
															starlink?.status === 1
																? "border border-green-400 bg-green-dark text-green-400"
																: "border border-red-400 bg-red-dark text-red-400"
														}`}
													>
														{starlink?.status === 1 ? "ONLINE" : "OFFLINE"}
													</span>
												</div>
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Last update:</span>
													<span className="text-gray-400">
														{(() => {
															const subscription = starlink?.subscriptions?.find(
																(s) => s.month === currentMonth && s.year === currentYear,
															);

															if (!subscription?.last_update) return "N/A";

															if (subscription?.last_update) {
																const date = new Date(subscription?.last_update);

																const year = date.getFullYear();
																const month = String(date.getMonth() + 1).padStart(2, "0");
																const day = String(date.getDate()).padStart(2, "0");
																const hours = String(date.getHours()).padStart(2, "0");
																const minutes = String(date.getMinutes()).padStart(2, "0");
																const seconds = String(date.getSeconds()).padStart(2, "0");

																const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
																return formattedDate;
															}

															return "N/A";
														})()}
													</span>
												</div>

												{starlink?.ipv4 &&
													typeof starlink?.ipv4 === "object" &&
													starlink?.ipv4?.publicIp && (
														<div className="mb-2 flex justify-between">
															<span className="font-medium text-gray-300">Public IP:</span>
															<span className="text-gray-400">{starlink?.ipv4?.publicIp}</span>
														</div>
													)}
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Uptime:</span>
													<span className="text-gray-400">{starlink?.uptime}</span>
												</div>
												{(userrole === IS_ADMIN_NAME ||
													userrole === IS_ACTIVATION_NAME ||
													userparentrole === IS_ADMIN_ROLE ||
													userparentrole === IS_ACTIVATION_ROLE) && (
													<>
														{starlink?.plan?.name && (
															<div className="mb-2 flex justify-between">
																<span className="font-medium text-gray-300">Plan:</span>
																<span className="text-gray-400">{starlink?.plan?.name}</span>
															</div>
														)}

														{starlink?.acccount_number?.number && (
															<div className="mb-2 flex justify-between">
																<span className="font-medium text-gray-300">Account Number:</span>
																<span className="text-gray-400">
																	{starlink?.acccount_number?.number}
																</span>
															</div>
														)}
													</>
												)}
											</div>
										))}
									</div>
								)}
								<div className="col-span-2">
									<div
										className={`${hasStarlinks && vesselData.starlinks.length > 0 ? "px-4" : "p-4"}`}
									>
										{vesselData?.imo != null && (
											<div className="mb-2 flex justify-between">
												<span className="font-medium text-gray-300">IMO:</span>
												<span className="text-gray-400"> {vesselData?.imo}</span>
											</div>
										)}
										{hasSmartBoxs && (
											<>
												{vesselData.smartboxes[0]?.total_gb_cellular_usage != null && (
													<div className="mb-2 flex justify-between">
														<span className="font-medium text-gray-300">Cellular Usage:</span>
														<span className="text-gray-400">
															{Number(vesselData.smartboxes[0].total_gb_cellular_usage).toFixed(2)}{" "}
															GB
														</span>
													</div>
												)}

												{vesselData.smartboxes[0]?.cellular_status != null && (
													<div className="mb-2 flex justify-between">
														<span className="font-medium text-gray-300">Cellular Status:</span>
														<span className="me-2 rounded bg-blue-gray p-2 px-2.5 py-0.5 font-medium text-white text-xs">
															{vesselData.smartboxes[0].cellular_status}
														</span>
													</div>
												)}
											</>
										)}
										{vesselData?.bandwidth_type != null && (
											<>
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Corporate Usage:</span>

													<div className="flex flex-row items-center justify-center gap-2">
														<span className="text-gray-400">
															{vesselData?.total_vouchers_usage} {""}GB
														</span>
													</div>
												</div>
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Corporate Allowance:</span>

													<div className="flex flex-row items-center justify-center gap-2">
														<span className="text-gray-400">
															{vesselData?.corporate_allowance} {""}GB
														</span>
														<button
															type="button"
															onClick={(e) => {
																e.stopPropagation();
																handleIncreaseAllowance();
															}}
															className={`flex items-center justify-center rounded-md bg-blue-gray p-1 font-medium text-sm text-white transition-all duration-300 ease-in-out hover:bg-blue-gray hover:bg-opacity-40`}
														>
															<Tooltip
																content="Increase corporate allowance"
																className="!bg-dark-blue m-0 p-1 text-[11px]"
															>
																<PlusIcon className="h-3 w-3 text-white" />
															</Tooltip>
														</button>
													</div>
												</div>
											</>
										)}
									</div>
								</div>

								{hasIntelsats && (
									<div
										className={`custom-scrollbar col-span-2 overflow-y-auto ${
											vesselData?.intelsats.length > 2 ? "max-h-[300px]" : ""
										}`}
									>
										{vesselData?.intelsats.map((intelsat, index) => (
											<div
												key={intelsat.id}
												className={`px-4 pt-4 ${
													index !== vesselData.intelsats.length - 1
														? "border-gray-700 border-b"
														: ""
												}`}
											>
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Current Beam Name :</span>
													<span className="text-gray-400">{intelsat.currentBeamName}</span>
												</div>
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Current Satellite Name</span>
													<span className="text-gray-400">{intelsat.currentSatelliteName}</span>
												</div>
												<div className="mb-2 flex justify-between">
													<span className="font-medium text-gray-300">Sat0 Ip Address :</span>
													<span className="text-gray-400">{intelsat.sat0_ip}</span>
												</div>
											</div>
										))}
									</div>
								)}

								{/* Delete Confirmation Modal */}
								{showDeleteModal && (
									<ModalDelete
										isOpen={showDeleteModal}
										closeModal={() => setShowDeleteModal(false)}
										unitName={""}
										handleDelete={handleDelete}
									/>
								)}

								{/* Error Modal */}
								<ModalAlert
									isOpen={showErrorModal}
									type="error"
									closeModal={() => setShowErrorModal(false)}
									message={error}
								/>

								{/* Success Modal */}
								<ModalAlert
									isOpen={showSuccessModal}
									type="success"
									closeModal={() => setShowSuccessModal(false)}
								/>
							</CustomAccordion>
						</div>
						<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-1">
							{vesselData?.is_scap === false && (
								<CustomAccordion
									title="STARLINK TOTAL USAGE"
									id={"total-usage"}
									isOpen={openAccordions[`total-usage`]}
									toggleAccordion={() => toggleAccordion(`total-usage`)}
									classCss={`${openAccordions[`total-usage`] ? "bg-navbar-bg" : ""} border-0`}
									isMobile={isMobile}
								>
									<div
										className={`mt-4 mb-4 flex items-center justify-center rounded border-dark-blue bg-navbar-bg p-3`}
									>
										<span className="font-semibold text-base text-white">{totalUsage} GB</span>
									</div>
								</CustomAccordion>
							)}
							{vesselData?.is_scap === true && (
								<CustomAccordion
									title="SCAP TOTAL USAGE"
									id={"total-usage-scap"}
									isOpen={openAccordions[`total-usage-scap`]}
									toggleAccordion={() => toggleAccordion(`total-usage-scap`)}
									classCss={`${openAccordions[`total-usage-scap`] ? "bg-navbar-bg" : ""} border-0`}
									isMobile={isMobile}
								>
									<ProjectOverviewContent vesselData={vesselData} vessel_id={vesselData?.id} />
								</CustomAccordion>
							)}
						</div>

						{/* Use GrowthBook feature flags fror vessel access users */}
						{accessUsersVisible && [IS_ADMIN_ROLE, IS_DISTRIBUTOR_ROLE].includes(userroleId) && (
							<div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-1">
								<CustomAccordion
									title="VESSEL ACCESS USERS"
									id={"vessel-users-access"}
									isOpen={openAccordions[`vessel-users-access`]}
									toggleAccordion={() => toggleAccordion(`vessel-users-access`)}
									classCss={`${openAccordions[`vessel-users-access`] ? "bg-navbar-bg" : ""} border-0`}
									isMobile={isMobile}
								>
									<form className="">
										<div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-1 md:grid-cols-2">
											<SearchInput
												id="search"
												placeholder="Search"
												label="Search by"
												value={searchTerms.search}
												onChange={handleSearchChange}
												onClear={handleClearSearch}
											/>
										</div>
									</form>
									{loading ? (
										<div className="relative mt-4 flex h-64 items-center justify-center overflow-x-auto bg-navbar-bg shadow-md md:rounded-lg">
											<div className="h-16 w-16 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
										</div>
									) : usersData ? (
										<div className="relative mt-4 overflow-x-auto bg-navbar-bg shadow-md md:rounded-lg">
											<ListTable
												list={usersData}
												columns={columns}
												isAction={false}
												handleRowClick={handleRowClick}
											/>
											<div className="flex flex-col items-center justify-between gap-2 bg-dark-blue bg-opacity-40 px-2 pt-4 pb-4 text-blue-gray text-xs sm:flex-row sm:gap-0 sm:px-4 sm:text-sm">
												<span>
													Showing {startItemIndex} to {endItemIndex} of {totalItems} entries
												</span>
												<Pagination
													currentPage={currentPage}
													totalPages={totalPage}
													onPageChange={handlePageChange}
												/>
											</div>
										</div>
									) : (
										<div className="flex h-64 items-center justify-center">
											<span className="font-normal text-gray-400 text-sm leading-tight">
												No data available
											</span>
										</div>
									)}
								</CustomAccordion>
							</div>
						)}
					</>
				</Suspense>
			)}
			{corporateModalData.open && (
				<Suspense fallback={<div></div>}>
					<ModalCorporateAllowance
						isOpen={corporateModalData.open}
						closeModal={() =>
							setCorporateModalData({ open: false, vesselId: null, vesselName: null })
						}
						vesselId={vesselData?.id}
						vesselName={vesselData?.site_name}
						token={token}
						fetchConfigurations={fetchConfigurations}
					/>
				</Suspense>
			)}
		</div>
	);
};

export default OverviewSummary;
